import type { ThemeChoices } from "$/lib/types/General.types";
import type { KeyValueObject } from "$/lib/types/Graphs.types";
import {
  generateGraphDateRangeFromData,
  generateXAxisTicks,
  generateYAxisTicks,
} from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { numberFormatter } from "$/lib/utils/functions/util.functions";
import { Fragment, useMemo } from "react";
import {
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { AxisDomain } from "recharts/types/util/types";
import { customColors } from "tailwind.config";
import type { GraphFilterItemsProps } from "../GraphFilter/GraphFilterItem";
import CustomTooltip from "../LineGraph/CustomTooltip";

type Props = {
  data: KeyValueObject[];
  unit?: string;
  hasNumberFormatter?: boolean;
  xDataKey: string;
  areaKeys: string[];
  lineKey: string;
  areaGradientKey?: string;
  colors: Record<string, string>;
  domain?: AxisDomain;
  theme?: ThemeChoices;
  leftMargin?: number;
  displayNames?: Record<string, string>;
  stops?: [string, string, string][];
  graphVisibility?: GraphFilterItemsProps[];
  heightPercent?: number;
  areaChartsFillOpacity?: number;
  hideKeys?: string[];
  notVisibleKeys?: string[];
};

const ComposedAreaGraph = ({
  data,
  xDataKey,
  hasNumberFormatter = false,
  areaKeys,
  lineKey,
  colors,
  areaGradientKey,
  domain,
  unit = " €/MWh",
  theme = "blue",
  leftMargin = 0,
  displayNames,
  stops,
  // heightPercent,
  hideKeys,
  areaChartsFillOpacity = 1,
  notVisibleKeys,
}: Props) => {
  // const { height: screenHeight } = useScreen();

  // const graphHeight = useMemo(
  //   () =>
  //     heightPercent
  //       ? Math.round(screenHeight * CONTAINER_HEIGHT_PERCENTAGE) *
  //         (heightPercent / 100)
  //       : 500,
  //   [heightPercent, screenHeight],
  // );

  const dateRange = useMemo(
    () => generateGraphDateRangeFromData(data, "dd/MM/yy", xDataKey),
    [data, xDataKey],
  );

  const xDomain = useMemo(
    () =>
      dateRange && dateRange[0] && dateRange[1]
        ? generateXAxisTicks(dateRange[0], dateRange[1])
        : undefined,
    [dateRange],
  );

  return (
    <ResponsiveContainer width="100%" minHeight={100}>
      <ComposedChart
        data={data}
        margin={{ top: 10, right: 30, left: leftMargin, bottom: 0 }}
      >
        <XAxis
          tickMargin={5}
          dataKey={xDataKey}
          fontSize={12}
          interval={0}
          ticks={xDomain}
        />

        <YAxis
          tickSize={2}
          width={85}
          tickMargin={5}
          fontWeight={500}
          fontSize={12}
          unit={unit}
          domain={domain}
          tickFormatter={hasNumberFormatter ? numberFormatter : undefined}
          interval={0}
          ticks={
            Array.isArray(domain)
              ? generateYAxisTicks(Number(domain[0]), Number(domain[1]))
              : undefined
          }
        />

        {areaKeys.map((key) => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            stroke="none"
            fill={colors[key]}
            connectNulls
            dot={false}
            fillOpacity={areaChartsFillOpacity}
            activeDot={false}
          />
        ))}

        {stops && (
          <defs>
            <linearGradient id="splitColor" x1="0" y1="0" x2="100%" y2="0">
              {stops.map(([offset, toOffset, color]) => {
                const offsetNum = parseFloat(offset);
                const toOffsetNum = parseFloat(toOffset);
                const adjustedToOffset = (toOffsetNum - 0).toFixed(3) + "%"; // nudge back
                const adjustedOffset = (offsetNum + 0).toFixed(3) + "%"; // nudge forward

                return (
                  <Fragment key={offset + "-" + toOffset}>
                    <stop offset={offset} stopColor={color} stopOpacity={1} />
                    <stop
                      offset={adjustedOffset}
                      stopColor={color}
                      stopOpacity={1}
                    />
                    <stop
                      offset={adjustedToOffset}
                      stopColor={color}
                      stopOpacity={1}
                    />
                    <stop offset={toOffset} stopColor={color} stopOpacity={1} />
                  </Fragment>
                );
              })}
            </linearGradient>
          </defs>
        )}

        {areaGradientKey && (
          <Area
            type="monotone"
            dataKey={areaGradientKey}
            stroke="none"
            fill={`url(#splitColor)`}
            dot={false}
            fillOpacity={1}
            activeDot={false}
          />
        )}

        <Line
          type="monotone"
          dataKey={lineKey}
          stroke={colors[lineKey]}
          connectNulls
          strokeWidth={2}
          dot={false}
          activeDot={false}
        />

        <Tooltip
          content={
            <CustomTooltip
              unit={unit}
              theme={theme}
              displayNames={displayNames}
              hideKeys={hideKeys}
            />
          }
        />

        {(notVisibleKeys ?? []).map((key) => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            stroke="none"
            fill={colors[key]}
            connectNulls
            dot={false}
            fillOpacity={0}
            activeDot={false}
          />
        ))}

        <ReferenceLine
          y={0}
          stroke={customColors.tertiary.DEFAULT}
          strokeDasharray={5}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default ComposedAreaGraph;
